import { Grid } from '@tremor/react';
import KpiTile from './KpiTile';
import AgingMiniBar from './AgingMiniBar';
import { formatMxn, formatInt, formatPctDelta } from '../lib/format';
import { t } from '../lib/i18n';
import type { Kpis } from '../api/types';
import type { DeltaType } from '@tremor/react';

interface Props {
  kpis: Kpis;
  showMargin: boolean;
}

function deltaType(current: number, previous: number): DeltaType {
  if (previous === 0) return 'unchanged';
  const pct = (current - previous) / previous;
  if (pct > 0.02) return 'moderateIncrease';
  if (pct < -0.02) return 'moderateDecrease';
  return 'unchanged';
}

// Tile config — declared once so the JSX stays flat and the per-role
// substitution (margin vs no-margin, inventory vs low-stock) reads as a
// sequence of conditional entries instead of three nested ternaries.
export default function KpiRow({ kpis, showMargin }: Props) {
  const tiles: Array<{
    key: string;
    label: string;
    value: string;
    sub?: string;
    delta?: { text: string; type: DeltaType };
    to?: string;
    extra?: React.ReactNode;
  } | null> = [
    {
      key: 'sales',
      label: t.kpi.salesToday,
      value: formatMxn(kpis.sales_today_mxn),
      sub: `${formatInt(kpis.ticket_count_today)} ${t.kpi.tickets}`,
      delta: {
        text: `${formatPctDelta(kpis.sales_today_mxn, kpis.sales_yesterday_mxn)} ${t.kpi.vsYesterday}`,
        type: deltaType(kpis.sales_today_mxn, kpis.sales_yesterday_mxn),
      },
      to: '/sales',
    },
    showMargin && kpis.gross_margin_pct_today !== null
      ? {
          key: 'margin',
          label: t.kpi.grossMargin,
          value: `${kpis.gross_margin_pct_today.toFixed(1)}%`,
        }
      : null,
    {
      key: 'ar',
      label: t.kpi.arTotal,
      value: formatMxn(kpis.ar_total_mxn),
      // AR aging visualized as a horizontal stacked bar — better at
      // signaling collections concern than the previous one-line text.
      extra: <AgingMiniBar buckets={kpis.ar_aging} />,
      to: '/ar',
    },
    kpis.inventory_cost_mxn !== null
      ? {
          key: 'inv',
          label: t.kpi.inventory,
          value: formatMxn(kpis.inventory_cost_mxn),
          sub: `${formatInt(kpis.low_stock_count)} ${t.kpi.lowStock}`,
          to: '/inventory',
        }
      : {
          key: 'low_stock',
          label: t.kpi.lowStockMetric,
          value: formatInt(kpis.low_stock_count),
          sub: t.kpi.parts,
          to: '/parts?kind=low',
        },
  ];

  return (
    <Grid numItemsSm={2} numItemsLg={4} className="gap-4">
      {tiles.filter(Boolean).map((tile) => (
        <KpiTile
          key={tile!.key}
          label={tile!.label}
          value={tile!.value}
          sub={tile!.sub}
          extra={tile!.extra}
          delta={tile!.delta}
          to={tile!.to}
        />
      ))}
    </Grid>
  );
}
